/**
 * paymentRoute — the ONE declaration of how this build is allowed to take money.
 *
 * Why it exists: the app has fourteen places that start or show a payment, and
 * on 2026-09-04 the Capacitor Android build shipped all of them wired to
 * Paddle. Paddle cannot run inside a store build — an outside payment route for
 * digital goods is a hard rejection at review by both Apple and Google — so
 * pressing Pay did nothing. The fix is not fourteen `if`s. It is one question,
 * answered here, that every affordance asks.
 *
 * The answer is derived from the platform seam (platform/capabilities.ts) and
 * nothing else. There is deliberately no list of screens, no per-component
 * check, and no independent env var for the route: add a payment surface
 * tomorrow and it inherits the right behaviour by asking, or it is wrong.
 * (Whether the store route is switched on is read from the native build's
 * allowlist, never from the environment; plan 034.)
 *
 *   web  → 'paddle'  (institutions, B2C on the web — unchanged, byte for byte)
 *   webview → 'store' (Google Play Billing now, StoreKit later)
 *
 * TWO SEPARATE QUESTIONS, and conflating them is the bug that got us here:
 *
 *   1. WHICH route is this build's route          → paymentRoute()
 *   2. Is that route actually WIRED yet           → canTakePayment()
 *
 * (2) is true only in a shell at STORE_BILLING_NATIVE_LEVEL or above whose
 * native build names 'store-billing' in its allowlist (capacitor.config.ts
 * SHELL_ALLOWLIST, plan 034). No environment variable can make it true:
 * Apple 2.5.19 forbids a feature the website switches on after review.
 *
 * A control that calls a route which is not wired is a dead button: a broken
 * promise to the learner and a rejection risk at review. So every affordance
 * gates on canTakePayment(), not on the route name, and storeBillingLive()
 * below decides it.
 *
 * INSTITUTIONAL / SEAT PURCHASE IS DIFFERENT AGAIN. It is not "pending" in a
 * store build — it must never exist there at all, at any point, because a seat
 * purchase is inherently an outside-payment route and even a hidden one is a
 * flag somebody can flip. So it is a BUILD-TIME constant, read from the same
 * VITE_APP_SHELL that names the shell, and the router does not register the
 * routes when it is false — the code is not in the bundle to be reached.
 */
import { isNativeShell, platform, shellAllows } from './capabilities'

/** Which payment rail this build is allowed to use. */
export type PaymentRoute = 'paddle' | 'store'

/**
 * The shell level that carries the billing plugin (capacitor.config.ts
 * SHELL_NATIVE_LEVEL 4). An older shell has no plugin, so its buttons stay off.
 */
export const STORE_BILLING_NATIVE_LEVEL = 4

/** The payment rail for the shell this build is running inside. */
export function paymentRoute(): PaymentRoute {
  return isNativeShell() ? 'store' : 'paddle'
}

/**
 * Can THIS shell take a store payment right now? Only where the native build
 * names 'store-billing' in its allowlist (plan 034) AND the shell carries the
 * billing plugin. No environment variable, injected stamp or server value can
 * make this true. An unknown os ('') is "store billing off", never a guess.
 *
 * EMERGENCY STOP: a hotfix that makes this return false reaches every install
 * on next launch. That only removes billing, so it never shows anything
 * review did not approve. Never add a web switch that can turn it ON.
 */
export function storeBillingLive(): boolean {
  if (!isNativeShell()) return false
  const { os, nativeLevel } = platform()
  if (os !== 'ios' && os !== 'android') return false
  return shellAllows('store-billing') && nativeLevel >= STORE_BILLING_NATIVE_LEVEL
}

/**
 * Which store THIS app's own billing belongs to: 'play' in the Android app,
 * 'apple' in the iPhone and iPad app, null on the web and in a shell that did
 * not say its OS (plan 039). Copy that names a store, and the row that opens a
 * store's own manage page, ask this rather than reading the platform: a
 * subscription is managed only in the store that holds it, so the Manage row
 * is for that store's app alone.
 */
export function shellStore(): 'apple' | 'play' | null {
  if (!isNativeShell()) return null
  const { os } = platform()
  return os === 'android' ? 'play' : os === 'ios' ? 'apple' : null
}

/**
 * True only on the web rail. Every door that would open Paddle, or mail a
 * Paddle pay link to somebody else, asks THIS rather than canTakePayment():
 * in an app where store billing is live canTakePayment() is true, and Paddle
 * must still be unreachable.
 */
export function canTakePaddlePayment(): boolean {
  return paymentRoute() === 'paddle'
}

/**
 * Can this build actually complete a consumer purchase right now?
 *
 * True on the web (Paddle, live). In a native shell, true only where
 * storeBillingLive(), and then only for SSi Premium through the store.
 * Every "Subscribe" / "Go Premium" / "Pay" control asks THIS.
 */
export function canTakePayment(): boolean {
  return paymentRoute() === 'paddle' || storeBillingLive()
}

/**
 * May this build show the web's subscription price ('£15/mo')?
 *
 * Only on the Paddle rail. Inside a store app a price with no purchase beside
 * it reads to a reviewer as pointing at an outside route (Apple 3.1.1), and the
 * store's own price, once billing is wired, is not the web's number anyway
 * (Tom, 2026-09-28, job #527).
 */
export function showsWebPrice(): boolean {
  return paymentRoute() === 'paddle'
}

/**
 * Is Paddle's own account machinery — the hosted customer portal, invoices,
 * card updates, in-app cancellation — reachable from this build?
 *
 * A separate question from canTakePayment() on purpose: when part 2 lands,
 * a native build CAN take payment (through Play) while these stay false,
 * because a Play subscription is cancelled and invoiced by Play, never by
 * Paddle. One declaration, two honest answers.
 */
export function paddleBillingAvailable(): boolean {
  return paymentRoute() === 'paddle'
}

/**
 * Build-time: does this BUILD contain institutional / seat purchase at all?
 *
 * Deliberately not a runtime predicate, and deliberately a vite `define`
 * (`__INSTITUTIONAL_PURCHASE__`, set from VITE_APP_SHELL in vite.config.js)
 * rather than `import.meta.env`: Vite replaces `import.meta.env` with the whole
 * env OBJECT, so a key lookup off it is a property access Rollup cannot fold
 * and the branch survives into the bundle — measured, not assumed. A define is
 * a textual literal, so in a webview build this folds to `false` and every
 * branch guarded by it — the /schools/upgrade,
 * /org/upgrade and /tutors/dashboard/upgrade routes, and the lazy import of
 * UpgradeView they hang off — is dead code Rollup drops. There is nothing left
 * in the artifact to flip.
 *
 * Verified by `paymentRoute.test.ts` (routes absent) and by
 * `e2e/_payment-route-bundle-check.mjs` (no seat-purchase strings in a
 * VITE_APP_SHELL=webview build).
 */
export const INSTITUTIONAL_PURCHASE_IN_BUILD: boolean =
  typeof __INSTITUTIONAL_PURCHASE__ === 'boolean' ? __INSTITUTIONAL_PURCHASE__ : true

/**
 * THE answer for anything that RENDERS an institutional affordance — a nav tab,
 * a billing panel, a trial-expired wall, an Upgrade link.
 *
 * Visibility and capability are one fact, and this is where it is declared.
 * Deriving visibility from INSTITUTIONAL_PURCHASE_IN_BUILD instead is the
 * defect an outside review found on 2026-09-05: the constant is BUILD-time, so
 * in the case the Android shell actually uses — the WEB artifact loaded inside
 * a WebView, with `window.__SSI_PLATFORM__ = { shell: 'webview' }` injected at
 * boot, which capabilities.ts documents as a first-class path — the constant is
 * true while the capability is false. The panel rendered, Subscribe worked, and
 * Paddle opened inside a store app: an outside payment route for digital goods,
 * and a hard rejection at review.
 *
 * So the split is: the constant removes code from the ARTIFACT (routes, lazy
 * imports); this predicate decides what a learner or tutor SEES. Pinned by
 * `institutionalVisibility.test.ts`, which scans the source rather than any one
 * component, because the failure mode is the next surface written the old way.
 */
export function institutionalPurchaseAvailable(): boolean {
  return INSTITUTIONAL_PURCHASE_IN_BUILD && paymentRoute() === 'paddle'
}

/**
 * The web pages whose whole job is a Paddle purchase: a school's or an
 * organisation's seats, and a parent paying for a pupil. Inside a store app
 * none of them may open (Google Play Payments policy; Apple 3.1.1). The tutor
 * lane's own upgrade page is closed by platform/tutorLane.ts already.
 *
 * /with/:code is deliberately NOT here: it is the class join link, and it also
 * takes free and already-paying joins. In a store app it stays open with its
 * price block hidden (showsWebPrice) and its paid join refused (plan 037).
 */
const WEB_PURCHASE_PREFIXES = ['/schools/upgrade', '/org/upgrade', '/pay-for']

/** Is this path a web purchase page? Whole segment only, any case, as Vue Router matches. */
export function isWebPurchasePath(path: string): boolean {
  const lower = path.toLowerCase()
  return WEB_PURCHASE_PREFIXES.some((p) => lower === p || lower.startsWith(`${p}/`))
}

/** A web purchase page this build must not open. The router sends it to the learner home. */
export function isClosedWebPurchasePath(path: string): boolean {
  return !canTakePaddlePayment() && isWebPurchasePath(path)
}
